import { ROLES, type Role, type User } from '@/entities/user';

/** Persisted session user (email/username) — cleared with tokens on logout. */
export const USER_SNAPSHOT_KEY = 'byq.user';

function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function normalizeStoreIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((id): id is string => typeof id === 'string' && id.length > 0);
}

function isDashboardRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

export function getPersistedUserSnapshot(): User | null {
  const raw = storage()?.getItem(USER_SNAPSHOT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<User>;
    if (parsed && typeof parsed.id === 'string' && isDashboardRole(parsed.role)) {
      return {
        id: parsed.id,
        username: typeof parsed.username === 'string' ? parsed.username : parsed.id,
        name: typeof parsed.name === 'string' ? parsed.name : parsed.id,
        role: parsed.role,
        storeIds: normalizeStoreIds(parsed.storeIds),
      };
    }
  } catch {
    return null;
  }
  return null;
}

export function setPersistedUserSnapshot(user: User | null): void {
  const s = storage();
  if (!s) return;
  if (!user) {
    s.removeItem(USER_SNAPSHOT_KEY);
    return;
  }
  s.setItem(USER_SNAPSHOT_KEY, JSON.stringify(user));
}
